import { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { useRollbar } from '@rollbar/react';
import isEqual from 'lodash/isEqual';

import useAuthentication from '../hooks/useAuthentication';

const QualityContentsContext = createContext();

export const QualityContentsProvider = ({ children }) => {
    const rollbar = useRollbar();
    const { currentUser } = useAuthentication();

    const cacheSeconds = 60;

    const [lastUpdated, setLastUpdated] = useState(0);

    const [qmsDocuments, _setQmsDocuments] = useState([]);
    const [qaDocuments, _setQaDocuments] = useState([]);
    const [qaRecords, _setQaRecords] = useState([]);

    const [isQmsDocumentsLoaded, setIsQmsDocumentsLoaded] = useState(false);
    const [isQaDocumentsLoaded, setIsQaDocumentsLoaded] = useState(false);
    const [isQaRecordsLoaded, setIsQaRecordsLoaded] = useState(false);

    const reloadInProgressRef = useRef(false);
    const abortControllerRef = useRef(null);

    // Clean up old localStorage keys (migration from old implementation)
    useEffect(() => {
        localStorage.removeItem('quality_contents_last_updated');
        localStorage.removeItem('quality_contents_qms_documents');
        localStorage.removeItem('quality_contents_qa_documents');
        localStorage.removeItem('quality_contents_qa_records');
    }, []);

    const _processQmsDocument = useCallback((row) => {
        const item = {};
        item.id = row.id;
        item.reference = row.reference;
        item.title = row.title;
        item.owner_user_id = row.owner_user_id;
        item.created_at = row.created_at;
        item.final_version_file_path = row.final_version_file_path;
        item.area_name = row.area.name;
        item.iso_section_number = row.iso_section_number;
        item.qms_document_type_name = row.qms_document_type.name;
        item.qms_document_status_name = row.qms_document_status.name;
        return item;
    }, []);

    const _processQaDocument = useCallback((row) => {
        const item = {};
        item.id = row.id;
        item.reference = row.reference;
        item.title = row.title;
        item.owner_user_id = row.owner_user_id;
        item.created_at = row.created_at;
        item.final_version_file_path = row.final_version_file_path;
        item.area_name = row.area.name;
        item.iso_section_number = row.iso_section_number;
        item.qa_document_type_name = row.qa_document_type.name;
        item.qa_document_status_name = row.qa_document_status.name;
        return item;
    }, []);

    const _processQaRecord = useCallback((row) => {
        const item = {};
        item.id = row.id;
        item.reference = row.reference;
        item.title = row.title;
        item.owner_user_id = row.owner_user_id;
        item.created_at = row.created_at;
        item.final_version_file_path = row.final_version_file_path;
        item.qa_record_type_name = row.qa_record_type.name;
        item.qa_record_status_name = row.qa_record_status.name;
        return item;
    }, []);

    const _reload = useCallback((objType, setCb, processCb, setLoadedCb, signal) => {
        return fetch('/v0/' + objType, { credentials: 'same-origin', signal })
            .then(function(response) {
                if (!response.ok) {
                    return response.text().then((bodyText) => {
                        const error = new Error(`HTTP error ${response.status}`);
                        error.response = response;
                        error.body = bodyText;
                        throw error;
                    });
                }
                return response.json();
            })
            .then(function(json) {
                if (signal && signal.aborted) return;
                if (json.error && json.error === 'ENOACCESS') {
                    setCb([]);
                    if (setLoadedCb) setLoadedCb(true);
                    return;
                }
                const data = json.results.map((row) => {
                    return processCb(row);
                });
                setCb((prev) => {
                    if (isEqual(data, prev)) {
                        return prev;
                    }
                    return data;
                });
                if (setLoadedCb) setLoadedCb(true);
            })
            .catch(error => {
                if (error.name === 'AbortError') return;
                if (error?.response?.status === 403) {
                    // Just ignore this, they don't have access
                    return;
                }
                rollbar?.error?.("useQualityContents reload error", error, {
                    objType: objType,
                    currentUserId: currentUser?.id,
                    status: error?.response?.status,
                    body: error?.body,
                });
                if (setLoadedCb) setLoadedCb(true);
            });
    }, [currentUser, rollbar]);

    const _reloadQmsDocuments = useCallback((signal) => _reload('qms_documents', _setQmsDocuments, _processQmsDocument, setIsQmsDocumentsLoaded, signal), [_reload, _processQmsDocument]);
    const _reloadQaDocuments = useCallback((signal) => _reload('qa_documents', _setQaDocuments, _processQaDocument, setIsQaDocumentsLoaded, signal), [_reload, _processQaDocument]);
    const _reloadQaRecords = useCallback((signal) => _reload('qa_records', _setQaRecords, _processQaRecord, setIsQaRecordsLoaded, signal), [_reload, _processQaRecord]);

    const reloadQualityContents = useCallback(() => {
        // If a reload is already in progress, abort it so the new one wins
        if (reloadInProgressRef.current) {
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
        }

        const controller = new AbortController();
        abortControllerRef.current = controller;
        reloadInProgressRef.current = true;

        setIsQmsDocumentsLoaded(false);
        setIsQaDocumentsLoaded(false);
        setIsQaRecordsLoaded(false);

        setLastUpdated(Date.now());

        Promise.all([
            _reloadQmsDocuments(controller.signal),
            _reloadQaDocuments(controller.signal),
            _reloadQaRecords(controller.signal),
        ]).finally(() => {
            if (abortControllerRef.current === controller) {
                reloadInProgressRef.current = false;
            }
        });
    }, [_reloadQmsDocuments, _reloadQaDocuments, _reloadQaRecords]);

    const clearQualityContents = useCallback(() => {
        _setQmsDocuments([]);
        _setQaDocuments([]);
        _setQaRecords([]);
        setLastUpdated(0);
        setIsQmsDocumentsLoaded(false);
        setIsQaDocumentsLoaded(false);
        setIsQaRecordsLoaded(false);
    }, []);

    // Auto-reload when the user is available and the cache has expired
    useEffect(() => {
        if (!currentUser?.id) return;
        if (lastUpdated + (1000 * cacheSeconds) > Date.now()) return;
        reloadQualityContents();
    }, [currentUser, lastUpdated, cacheSeconds, reloadQualityContents]);

    // Abort any pending fetches when the provider unmounts
    useEffect(() => {
        return () => {
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
        };
    }, []);

    const _updateOrInsert = useCallback((list, data) => {
        const index = list.findIndex(item => item.id === data.id);
        if (index >= 0) {
            const updated = [...list];
            updated[index] = data;
            return updated;
        }
        return [...list, data];
    }, []);

    const _update = useCallback((input, process, setCb) => {
        const data = process(input);
        setCb((prev) => _updateOrInsert(prev, data));
    }, [_updateOrInsert]);

    const _delete = useCallback((id, setCb) => {
        setCb((prev) => prev.filter(item => item.id !== id));
    }, []);

    const updateQmsDocument = useCallback((item) => _update(item, _processQmsDocument, _setQmsDocuments), [_update, _processQmsDocument]);
    const deleteQmsDocument = useCallback((id) => _delete(id, _setQmsDocuments), [_delete]);
    const updateQaDocument = useCallback((item) => _update(item, _processQaDocument, _setQaDocuments), [_update, _processQaDocument]);
    const deleteQaDocument = useCallback((id) => _delete(id, _setQaDocuments), [_delete]);
    const updateQaRecord = useCallback((item) => _update(item, _processQaRecord, _setQaRecords), [_update, _processQaRecord]);
    const deleteQaRecord = useCallback((id) => _delete(id, _setQaRecords), [_delete]);

    const forceReloadQmsDocuments = useCallback(async () => {
        await _reloadQmsDocuments();
    }, [_reloadQmsDocuments]);

    const forceReloadQaDocuments = useCallback(async () => {
        await _reloadQaDocuments();
    }, [_reloadQaDocuments]);

    const value = {
        isQmsDocumentsLoaded,
        isQaDocumentsLoaded,
        isQaRecordsLoaded,

        reloadQualityContents,
        clearQualityContents,

        qmsDocuments,
        qaDocuments,
        qaRecords,

        forceReloadQmsDocuments,
        forceReloadQaDocuments,

        updateQmsDocument,
        deleteQmsDocument,
        updateQaDocument,
        deleteQaDocument,
        updateQaRecord,
        deleteQaRecord,
    };

    return (
        <QualityContentsContext.Provider value={value}>
            {children}
        </QualityContentsContext.Provider>
    );
};

export const useQualityContentsContext = () => {
    const context = useContext(QualityContentsContext);
    if (!context) {
        throw new Error('useQualityContentsContext must be used within a QualityContentsProvider');
    }
    return context;
};
